/** @jsxImportSource @emotion/react */
import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  TextField,
  Grid,
  Switch,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Snackbar,
  FormControlLabel,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { useEventData } from "../EventDataContext";
import RedeemIcon from '@mui/icons-material/Redeem';
import SendIcon from '@mui/icons-material/Send';

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    transition: "all 0.2s ease-in-out",
    "& fieldset": {
      border: "1px solid #E0E0E0",
    },
    "&:hover fieldset": {
      border: "1px solid #BDBDBD",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #8A5918",
      boxShadow: "0 0 0 3px rgba(138, 89, 24, 0.1)",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
    },
  },
}));

const SectionTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
    <Box sx={{ width: '4px', height: '24px', backgroundColor: '#8A5918', mr: 1, borderRadius: '2px' }} />
    <Typography
      variant="h6"
      sx={{
        color: "#1A1A1A",
        fontWeight: 700,
        fontSize: "18px",
        textAlign: "left",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Label = ({ children, required }) => (
  <Typography
    sx={{
      color: "#4A4A4A",
      fontWeight: 600,
      fontSize: "14px",
      mb: 1,
      textAlign: "left",
    }}
  >
    {children} {required && <span style={{ color: "#E94C4C" }}>*</span>}
  </Typography>
);

export default function GiftSettingsPage() {
  const { eventData, updateNestedEventData, uploadImageAndUpdateData, validationErrors } = useEventData();

  const giftData = eventData?.gift || {};
  const backgroundImage = giftData?.backgroundImage?.value || null;
  const giftButtonEnabled = giftData?.giftButton?.enabled?.value || false;
  const giftButtonIcon = giftData?.giftButton?.icon?.value || "star";
  const giftButtonText = giftData?.giftButton?.text?.value || "";
  const groomBrideEnabled = giftData?.giftReceiverSetup?.groomBrideToggle?.value || false;
  const nameFieldEnabled = giftData?.formSettings?.nameField?.enabled?.value || false;
  const namePlaceholder = giftData?.formSettings?.nameField?.placeholder?.value || "";
  const uploadSlipEnabled = giftData?.formSettings?.uploadSlip?.value || false;
  const allowAnonymousEnabled = giftData?.formSettings?.allowAnonymous?.enabled?.value || false;
  const anonymousText = giftData?.formSettings?.allowAnonymous?.text?.value || "";
  const confirmButtonEnabled = giftData?.confirmButton?.enabled?.value || false;
  const confirmButtonIcon = giftData?.confirmButton?.icon?.value || "star";
  const confirmButtonText = giftData?.confirmButton?.text?.value || "";
  const accountName = giftData?.accounts?.accountName?.value || "";
  const accountNumber = giftData?.accounts?.accountNumber?.value || "";
  const accountBank = giftData?.accounts?.accountBank?.value || "";

  const [uploadingImage, setUploadingImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const hasError = (fieldName) => {
    return validationErrors?.gift?.[fieldName];
  };

  const getErrorMessage = (fieldName) => {
    return validationErrors?.gift?.[fieldName] || '';
  };

  const hasNestedError = (section, fieldName) => {
    return validationErrors?.gift?.[section]?.[fieldName];
  };

  const getNestedErrorMessage = (section, fieldName) => {
    return validationErrors?.gift?.[section]?.[fieldName] || '';
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadingImage(true);
      try {
        const result = await uploadImageAndUpdateData("gift", "backgroundImage", file);
        if (!result.success) {
          if (!result.error.includes('size')) {
            const reader = new FileReader();
            reader.onloadend = () => {
              updateNestedEventData('gift', 'backgroundImage', { value: reader.result });
            };
            reader.readAsDataURL(file);
          } else {
            setErrorMessage(result.error);
            setShowError(true);
          }
        }
      } catch (error) {
        setErrorMessage("An unexpected error occurred while uploading the image");
        setShowError(true);
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const removeImage = () => {
    updateNestedEventData('gift', 'backgroundImage', { value: null });
  };

  const iconOptions = [
    { value: "star", icon: <StarIcon />, label: "Star" },
    { value: "heart", icon: <FavoriteIcon />, label: "Heart" },
    { value: "thumbup", icon: <ThumbUpIcon />, label: "Thumb Up" },
    { value: "redeem", icon: <RedeemIcon />, label: "Redeem" },
    { value: "send", icon: <SendIcon />, label: "Send" },
  ];

  return (
    <Box>
      <SectionTitle>Gift Background Image</SectionTitle>
      <Box
        sx={{
          position: "relative",
          backgroundColor: "#F9FAFB",
          border: "2px dashed #E0E0E0",
          borderRadius: "12px",
          p: 3,
          textAlign: "center",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "#8A5918",
            backgroundColor: "#FFF8F0",
          },
        }}
      >
        {backgroundImage ? (
          <>
            <img
              src={backgroundImage}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "300px",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
            <IconButton
              onClick={removeImage}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <AttachFileIcon sx={{ color: "#9CA3AF", fontSize: 48, mb: 2 }} />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="#4A4A4A"
              mb={0.5}
            >
              Click to upload image
            </Typography>
            <Typography variant="body2" color="#9CA3AF" mb={3}>
              SVG, PNG, JPG or GIF (max. 800KB)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              disabled={uploadingImage}
              sx={{
                color: "#8A5918",
                borderColor: "#8A5918",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#6b3b07",
                  backgroundColor: "rgba(138, 89, 24, 0.04)",
                },
              }}
            >
              {uploadingImage ? "Uploading..." : "Browse File"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploadingImage}
              />
            </Button>
          </Box>
        )}
      </Box>



      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      {giftButtonEnabled && (
        <>
          <SectionTitle>Bank Account Details</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Label required>Account Name</Label>
              <StyledTextField
                placeholder="Account Name"
                value={accountName}
                onChange={(e) => updateNestedEventData('gift', 'accounts', {
                  ...(giftData?.accounts || {}),
                  accountName: { value: e.target.value, required: false }
                })}
                error={hasNestedError('accounts', 'accountName')}
                helperText={getNestedErrorMessage('accounts', 'accountName')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label required>Account Number</Label>
              <StyledTextField
                placeholder="Account Number"
                value={accountNumber}
                onChange={(e) => updateNestedEventData('gift', 'accounts', {
                  ...(giftData?.accounts || {}),
                  accountNumber: { value: e.target.value, required: false }
                })}
                error={hasNestedError('accounts', 'accountNumber')}
                helperText={getNestedErrorMessage('accounts', 'accountNumber')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Label required>Bank Name</Label>
              <StyledTextField
                placeholder="Bank Name"
                value={accountBank}
                onChange={(e) => updateNestedEventData('gift', 'accounts', {
                  ...(giftData?.accounts || {}),
                  accountBank: { value: e.target.value, required: false }
                })}
                error={hasNestedError('accounts', 'accountBank')}
                helperText={getNestedErrorMessage('accounts', 'accountBank')}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <SectionTitle>Form Settings</SectionTitle>
      <Grid container spacing={3} alignItems="flex-start">
        {/* Row 1: Groom/Bride Toggle */}
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={2} height="56px" sx={{ mt: 3.5 }}>
            <Switch
              checked={groomBrideEnabled}
              onChange={(e) => updateNestedEventData('gift', 'giftReceiverSetup', {
                ...(giftData?.giftReceiverSetup || {}),
                groomBrideToggle: { value: e.target.checked, required: false }
              })}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                  opacity: 0.5,
                },
              }}
            />
            <Typography sx={{ fontWeight: 600, color: "#4A4A4A", fontSize: "14px" }}>
              Groom/Bride Toggle
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} />

        {/* Row 2: Upload Slip */}
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={2} height="56px" sx={{ mt: 3.5 }}>
            <Switch
              checked={uploadSlipEnabled}
              onChange={(e) => updateNestedEventData('gift', 'formSettings', {
                ...(giftData?.formSettings || {}),
                uploadSlip: { value: e.target.checked, required: false }
              })}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                  opacity: 0.5,
                },
              }}
            />
            <Typography sx={{ fontWeight: 600, color: "#4A4A4A", fontSize: "14px" }}>
              Upload Slip
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6} />
      </Grid>

      <Grid container spacing={3} sx={{ mt: 3.5 }}>
        {/* Row 3: Name Field */}
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={2} height="56px" sx={{ mt: 3.5 }}>
            <Switch
              checked={nameFieldEnabled}
              onChange={(e) => updateNestedEventData('gift', 'formSettings', {
                ...(giftData?.formSettings || {}),
                nameField: {
                  ...(giftData?.formSettings?.nameField || {}),
                  enabled: { value: e.target.checked, required: false }
                }
              })}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                  opacity: 0.5,
                },
              }}
            />
            <Typography sx={{ fontWeight: 600, color: "#4A4A4A", fontSize: "14px" }}>
              Name Field
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Label required={nameFieldEnabled}>Name Placeholder</Label>
          <StyledTextField
            placeholder="e.g. Enter your name"
            value={namePlaceholder}
            disabled={!nameFieldEnabled}
            onChange={(e) => updateNestedEventData('gift', 'formSettings', {
              ...(giftData?.formSettings || {}),
              nameField: {
                ...(giftData?.formSettings?.nameField || {}),
                placeholder: { value: e.target.value, required: true }
              }
            })}
            error={hasNestedError('formSettings', 'nameField')?.placeholder}
            helperText={hasNestedError('formSettings', 'nameField')?.placeholder || ''}
          />
        </Grid>

        {/* Row 4: Allow Anonymous */}
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={2} height="56px" sx={{ mt: 3.5 }}>
            <Switch
              checked={allowAnonymousEnabled}
              onChange={(e) => updateNestedEventData('gift', 'formSettings', {
                ...(giftData?.formSettings || {}),
                allowAnonymous: {
                  ...(giftData?.formSettings?.allowAnonymous || {}),
                  enabled: { value: e.target.checked, required: false }
                }
              })}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                  opacity: 0.5,
                },
              }}
            />
            <Typography sx={{ fontWeight: 600, color: "#4A4A4A", fontSize: "14px" }}>
              Allow Anonymous
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Label required={allowAnonymousEnabled}>Anonymous Text</Label>
          <StyledTextField
            placeholder="e.g. Keep me anonymous"
            value={anonymousText}
            disabled={!allowAnonymousEnabled}
            onChange={(e) => updateNestedEventData('gift', 'formSettings', {
              ...(giftData?.formSettings || {}),
              allowAnonymous: {
                ...(giftData?.formSettings?.allowAnonymous || {}),
                text: { value: e.target.value, required: true }
              }
            })}
            error={hasNestedError('formSettings', 'allowAnonymous')?.text}
            helperText={hasNestedError('formSettings', 'allowAnonymous')?.text || ''}
          />
        </Grid>
      </Grid>

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SectionTitle>Confirm Button</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={confirmButtonEnabled}
              onChange={(e) => updateNestedEventData('gift', 'confirmButton', {
                ...(giftData?.confirmButton || {}),
                enabled: { value: e.target.checked, required: false }
              })}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          }
          label={confirmButtonEnabled ? "Enabled" : "Disabled"}
        />
      </Box>

      {confirmButtonEnabled && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Label>Button Icon</Label>
            <FormControl fullWidth>
              <Select
                value={confirmButtonIcon}
                onChange={(e) => updateNestedEventData('gift', 'confirmButton', {
                  ...(giftData?.confirmButton || {}),
                  icon: { value: e.target.value, required: false }
                })}
                IconComponent={ExpandMoreIcon}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#8A5918",
                  },
                  "& .MuiSelect-select": {
                    padding: "12px 16px",
                  },
                }}
              >
                {iconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {option.icon}
                      <Typography>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={12}>
            <Label required={confirmButtonEnabled}>Button Text</Label>
            <StyledTextField
              placeholder="e.g. Confirm Gift"
              value={confirmButtonText}
              onChange={(e) => updateNestedEventData('gift', 'confirmButton', {
                ...(giftData?.confirmButton || {}),
                text: { value: e.target.value, required: false }
              })}
              error={hasNestedError('confirmButton', 'text')}
              helperText={getNestedErrorMessage('confirmButton', 'text')}
            />
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
